<template>
  <v-dialog
    :value="value"
    max-width="820"
    content-class="backside-dialog rounded-xl"
    overlay-opacity="0.75"
    transition="dialog-transition"
    @input="$emit('input', $event)"
  >
    <v-card dark class="backside-card rounded-xl">
      <div class="backside-header">
        <div class="backside-header-inner">
          <h2 class="backside-title">Backside Engraving</h2>
          <v-btn icon class="backside-close-btn" @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <v-card-text class="backside-modal-body">
        <v-row no-gutters class="backside-row">
          <v-col cols="12" md="6" class="backside-left-col">
            <div class="backside-form-panel">
              <LineItemPicker
                label="Line item"
                :items="lineItems"
                :value="selectedLineItemIndex"
                empty-text="No line items"
                @input="selectedLineItemIndex = $event"
              />
              <TextLinesInput
                label="Text lines"
                :value="textLines"
                :placeholders="['Line 1', 'Line 2', 'Line 3', 'Line 4']"
                @input="textLines = $event"
              />
              <FontSelect
                label="Font"
                :value="selectedFont"
                :items="fontOptions"
                @input="selectedFont = $event"
              />
              <v-btn
                color="primary"
                :loading="loading"
                block
                class="backside-generate-btn mt-1"
                @click="generateSvg"
              >
                <v-icon left small>mdi-auto-fix</v-icon>
                Generate preview
              </v-btn>
            </div>
          </v-col>
          <v-col cols="0" md="1" />
          <v-col cols="12" md="4" class="backside-preview-col">
            <SvgPreviewWithDownload
              label="Preview"
              :src="svgUrl"
              :download-filename="svgDownloadFilename || 'backside_engraving.svg'"
              placeholder-text="Your engraving will appear here"
              placeholder-hint="Fill in the text and click Generate"
              @download="$emit('download')"
            />
          </v-col>
          <v-col cols="0" md="1" />
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { createTextSvg } from '@/services/pdfGenerationService'
import { getBacksideEngravingLineItems, getBacksideEngravingFromLineItem, fontValueFromProp } from './orderHelpers'
import { BACKSIDE_ENGRAVING_LINE_ITEM_IDS, BACKSIDE_FONT_OPTIONS, BACKSIDE_SVG_OPTIONS } from './constants'
import LineItemPicker from './LineItemPicker.vue'
import TextLinesInput from './TextLinesInput.vue'
import FontSelect from './FontSelect.vue'
import SvgPreviewWithDownload from './SvgPreviewWithDownload.vue'

export default {
  name: 'BacksideEngravingModal',
  components: {
    LineItemPicker,
    TextLinesInput,
    FontSelect,
    SvgPreviewWithDownload,
  },
  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      selectedLineItemIndex: 0,
      textLines: ['', '', '', ''],
      selectedFont: 'modern',
      svgUrl: null,
      svgDownloadFilename: null,
      loading: false,
    }
  },
  computed: {
    fontOptions() {
      return BACKSIDE_FONT_OPTIONS
    },
    lineItems() {
      return getBacksideEngravingLineItems(this.order, BACKSIDE_ENGRAVING_LINE_ITEM_IDS)
    },
    selectedLineItem() {
      const list = this.lineItems
      const idx = this.selectedLineItemIndex
      return list[idx] ?? null
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.selectedLineItemIndex = 0
        this.prefillFromLineItem()
        this.svgUrl = null
        this.svgDownloadFilename = null
        this.$nextTick(() => this.generateSvg())
      }
    },
    selectedLineItemIndex() {
      this.prefillFromLineItem()
    },
  },
  methods: {
    prefillFromLineItem() {
      const li = this.selectedLineItem
      if (!li) {
        this.textLines = ['', '', '', '']
        this.selectedFont = 'modern'
        return
      }
      const props = getBacksideEngravingFromLineItem(li)
      this.textLines = [
        props.text1 ?? '',
        props.text2 ?? '',
        props.text3 ?? '',
        props.text4 ?? '',
      ]
      this.selectedFont = fontValueFromProp(props.font)
    },
    async generateSvg() {
      const lines = this.textLines
        .filter((t) => t != null && String(t).trim() !== '')
        .map((text) => ({ text: String(text).trim(), fontStyle: this.selectedFont }))
      if (!lines.length) return

      this.loading = true
      this.svgUrl = null
      this.svgDownloadFilename = null
      try {
        const orderNum = this.order?.orderNumber || 'order'
        const filename = `${orderNum}_back_${Date.now()}.svg`
        const res = await createTextSvg(
          { lines, options: BACKSIDE_SVG_OPTIONS },
          filename
        )
        const data = res?.data?.data ?? res?.data
        const url = data?.url
        if (url) {
          this.svgUrl = url
          this.svgDownloadFilename = data?.filename || filename
          this.$emit('generated', { url, filename: data?.filename || filename })
        }
      } catch (err) {
        console.error('Backside SVG generation failed:', err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.backside-dialog >>> .v-dialog {
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.04);
}

.backside-card {
  background: #1c1c1f !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.backside-header {
  padding: 0;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.backside-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
}

.backside-title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.95);
}

.backside-close-btn {
  min-width: 32px !important;
  width: 32px !important;
  height: 32px !important;
  min-height: 32px !important;
  border-radius: 50% !important;
  color: #fff !important;
  background-color: #e53935 !important;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.backside-close-btn .v-icon {
  font-size: 18px !important;
}

.backside-close-btn:hover {
  color: #fff !important;
  background-color: #f44336 !important;
}

.backside-close-btn:hover .v-icon {
  color: inherit !important;
}

.backside-modal-body {
  min-height: 300px;
  padding: 14px 16px 16px !important;
}

.backside-row {
  align-items: stretch;
}

.backside-left-col {
  padding-right: 20px;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}

.backside-form-panel {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 12px 14px;
}

.backside-form-panel >>> .line-item-picker,
.backside-form-panel >>> .text-lines-input,
.backside-form-panel >>> .font-select {
  margin-bottom: 12px;
}

.backside-form-panel >>> .font-select:last-of-type {
  margin-bottom: 0;
}

.backside-generate-btn {
  text-transform: none;
  font-weight: 600;
  font-size: 0.875rem !important;
  letter-spacing: 0.02em;
  border-radius: 8px !important;
}

@media (min-width: 960px) {
  .backside-preview-col {
    padding-left: 20px;
  }
}
</style>
